import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';

const RetouchTypesManagement = ({ modelId, onClose }) => {
  const [retouches, setRetouches] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRetouch, setEditingRetouch] = useState(null);
  const [formData, setFormData] = useState({
    model: modelId || '', name: '', description: '', is_active: true, image: null
  });

  const fetchData = useCallback(async () => {
    try {
      const url = modelId 
        ? `${API_URL}/api/retouch-types/?model=${modelId}`
        : `${API_URL}/api/retouch-types/`;
      const [retouchesRes, modelsRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API_URL}/api/models/`)
      ]);
      setRetouches(retouchesRes.data.results || retouchesRes.data);
      setModels(modelsRes.data.results || modelsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.model) {
        alert('Veuillez sélectionner un modèle');
        return;
      }
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '' && key !== 'image') {
          data.append(key, formData[key]);
        }
      });
      if (formData.image) {
        data.append('image', formData.image);
      }

      if (editingRetouch) {
        await axios.patch(`${API_URL}/api/retouch-types/${editingRetouch.id}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_URL}/api/retouch-types/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      fetchData();
      setShowForm(false);
      setEditingRetouch(null);
      setFormData({ model: modelId || '', name: '', description: '', is_active: true, image: null });
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || 'Erreur inconnue'));
    }
  };

  const handleEdit = (retouch) => {
    setEditingRetouch(retouch);
    setFormData({
      model: retouch.model || modelId || '',
      name: retouch.name,
      description: retouch.description || '',
      is_active: retouch.is_active,
      image: null
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce type de retouche ?')) {
      try {
        await axios.delete(`${API_URL}/api/retouch-types/${id}/`);
        fetchData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-12 w-12"></div>
      </div>
    );
  }


  return (
    <div className="space-y-6 animate-fade-in">
      <div className="section-header">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="section-title">
              Gestion des Retouches
              {modelId && <span className="text-lg text-gray-600 ml-2">(Filtré)</span>}
            </h2>
            <p className="section-subtitle">Créez et gérez les types de retouches</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowForm(true);
                setEditingRetouch(null);
                setFormData({ model: modelId || '', name: '', description: '', is_active: true, image: null });
              }}
              className="btn-primary flex items-center space-x-2"
            >
              <span>+</span>
              <span className="hidden sm:inline">Ajouter</span>
            </button>
            {onClose && (
              <button onClick={onClose} className="btn-secondary">
                Fermer
              </button>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingRetouch(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-corporate-900">
                {editingRetouch ? 'Modifier' : 'Ajouter'} une retouche
              </h3>
              <button
                onClick={() => { setShowForm(false); setEditingRetouch(null); }}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-600"
                aria-label="Fermer"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {!modelId && (
                <div>
                  <label className="block text-sm font-semibold text-corporate-700 mb-2">
                    Modèle *
                  </label>
                  <select
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    required
                    disabled={!!editingRetouch}
                    className="input-modern disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Sélectionner un modèle</option>
                    {models.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-modern"
                  placeholder="Nom du type de retouche"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Image
                </label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Prendre une photo */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                      className="hidden"
                      id="retouch-image-camera"
                    />
                    <label htmlFor="retouch-image-camera" className="cursor-pointer block">
                      <div className="text-3xl mb-2">📷</div>
                      <p className="text-xs text-gray-600 font-medium">Prendre une photo</p>
                    </label>
                  </div>
                  
                  {/* Uploader depuis galerie */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                      className="hidden"
                      id="retouch-image-gallery"
                    />
                    <label htmlFor="retouch-image-gallery" className="cursor-pointer block">
                      <div className="text-3xl mb-2">🖼️</div>
                      <p className="text-xs text-gray-600 font-medium">Depuis la galerie</p>
                    </label>
                  </div>
                </div>
                
                {formData.image && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      ✓ {formData.image.name}
                    </p>
                  </div>
                )}
                
                {editingRetouch && editingRetouch.image_url && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Image actuelle :</p>
                    <img src={editingRetouch.image_url} alt="Prévisualisation" className="rounded-lg shadow-corporate max-w-full sm:max-w-xs" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  className="input-modern"
                  placeholder="Description du type de retouche..."
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_active_retouch"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="is_active_retouch" className="text-sm font-semibold text-corporate-700">
                  Retouche active
                </label>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  {editingRetouch ? 'Enregistrer' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingRetouch(null); }}
                  className="btn-secondary flex-1"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {retouches.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-5xl mb-4">🔧</div>
          <h3 className="text-xl font-bold text-corporate-900 mb-2">Aucune retouche</h3>
          <p className="text-gray-600 mb-6">Commencez par créer votre première retouche</p>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingRetouch(null);
              setFormData({ model: modelId || '', name: '', description: '', is_active: true, image: null });
            }}
            className="btn-primary"
          >
            Créer une retouche
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {retouches.map((retouch, index) => (
            <div
              key={retouch.id}
              className="card-hover animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {retouch.image_url ? (
                <div className="relative overflow-hidden rounded-lg mb-4 h-40 sm:h-48 bg-gray-100">
                  <img
                    src={retouch.image_url}
                    alt={retouch.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-2 right-2">
                    {retouch.is_active ? (
                      <span className="badge-success">Actif</span>
                    ) : (
                      <span className="badge-danger">Inactif</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="relative h-40 sm:h-48 mb-4 rounded-lg bg-gray-100 flex items-center justify-center text-5xl sm:text-6xl">
                  🔧
                  <div className="absolute top-2 right-2">
                    {retouch.is_active ? (
                      <span className="badge-success">Actif</span>
                    ) : (
                      <span className="badge-danger">Inactif</span>
                    )}
                  </div>
                </div>
              )}

              <h3 className="text-lg font-bold text-corporate-900 mb-3">
                {retouch.name}
              </h3>

              {retouch.model_name && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase">Modèle</p>
                  <p className="text-sm text-gray-900">{retouch.model_name}</p>
                </div>
              )}

              {retouch.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{retouch.description}</p>
              )}

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(retouch)}
                  className="btn-primary flex-1 text-sm py-2"
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(retouch.id)}
                  className="btn-danger flex-1 text-sm py-2"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RetouchTypesManagement;
